import {
  BaseTableService,
  DEFAULT_TABLE_PAGE_SIZE,
  SortOrderEnum,
  TABLE_PAGE_SIZE_OPTIONS,
} from '@/services/ui/table/base-table.service';
import {
  DestroyRef,
  Type,
  computed,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, firstValueFrom } from 'rxjs';
import {
  MatTableActionsConfig,
  MatTableAppearance,
  MatTableChildRowsConfig,
  MatTableColumn,
  MatTableColumnReorderConfig,
  MatTableColumnResizePersistConfig,
  MatTableDragDropConfig,
  MatTableFetchFn,
  MatTableFilter,
  MatTablePaginationConfig,
  MatTableQuery,
  MatTableQueryFn,
  MatTableQueryPersistence,
  MatTableRowExpansionConfig,
  MatTableSelectionConfig,
} from './mat-table.types';
import {
  formatResolvedColumnValue,
  readColumnPath,
} from '@/services/data-structure/dso-to-many-cell.util';
import { MatRowDetailComponent } from './row-detail/mat-row-detail.component';

/** MatTable forbids duplicate `matColumnDef` names; keep the first occurrence. */
function uniqueColumnsById<T>(
  columns: MatTableColumn<T>[]
): MatTableColumn<T>[] {
  const seen = new Set<string>();
  return columns.filter(col => {
    if (seen.has(col.id)) return false;
    seen.add(col.id);
    return true;
  });
}

/**
 * Service for managing Mat Table state and operations
 * Implements BaseTableService for compatibility with the table factory
 * Similar pattern to GridTable for consistency with existing codebase
 */
export class MatTableService<T = any> implements BaseTableService<T> {
  private readonly destroyRef = inject(DestroyRef);

  // State signals
  readonly isLoading = signal<boolean>(false);
  readonly data = signal<T[]>([]);
  readonly totalCount = signal<number>(0);
  readonly expandedRows = signal<Set<T>>(new Set());
  readonly selectedRows = signal<Set<T>>(new Set());
  readonly printSubject = signal<string | null>(null);
  /** Row that is the "current" selection (last toggled) for dark-blue highlight when using multiple selection */
  readonly currentSelectionRow = signal<T | null>(null);
  readonly collapsedParents = signal<Set<T>>(new Set()); // Track collapsed parent rows for child rows feature

  /**
   * Internal parent map for tree / child-row functionality.
   * This is populated when data is flattened based on getChildren().
   */
  private readonly childParentMap = new Map<T, T>();

  // Column ID prefix for preventing conflicts between multiple tables
  private readonly _columnIdPrefix = signal<string>(
    crypto.randomUUID ? crypto.randomUUID() : this.generateUUID()
  );

  // Configuration signals
  private readonly _columns = signal<MatTableColumn<T>[]>([]);
  private readonly _pagination = signal<MatTablePaginationConfig>({
    enabled: false,
    pageSize: DEFAULT_TABLE_PAGE_SIZE,
    pageSizeOptions: [...TABLE_PAGE_SIZE_OPTIONS],
    showFirstLastButtons: true,
  });
  private readonly _rowExpansion = signal<MatTableRowExpansionConfig<T>>({
    enabled: false,
    expandOnRowClick: true,
    multipleExpanded: false,
    hideExpandColumn: false,
  });
  private readonly _childRows = signal<MatTableChildRowsConfig<T>>({
    enabled: false,
    getChildren: () => undefined,
    isParent: () => false,
    isChild: () => false,
    toggleOnRowClick: true,
    multipleExpanded: true,
    indentChildren: true,
    indentAmount: 40,
  });
  private readonly _selection = signal<MatTableSelectionConfig<T>>({
    enabled: false,
    mode: 'single',
    highlightSelected: true,
  });
  private readonly _actions = signal<MatTableActionsConfig<T>>({
    enabled: false,
  });
  /** Bumped when external state used in `action.visible` changes (e.g. CASL) so `hasVisibleActions` recomputes. */
  private readonly _actionsVisibilityRevision = signal(0);
  private readonly _appearance = signal<MatTableAppearance>({
    showRowNumbers: false, // Row number column hidden by default
    showRowHover: true,
    striped: true,
    bordered: false,
    dense: false,
    /** Default auto avoids zero-height table bodies when the host is not a bounded flex column. */
    heightMode: 'auto',
  });
  private readonly _dragDrop = signal<MatTableDragDropConfig<T>>({
    enabled: false,
  });
  private readonly _columnReorder = signal<MatTableColumnReorderConfig>({
    enabled: false,
  });
  private _columnResizePersist = signal<MatTableColumnResizePersistConfig>({
    enabled: false,
  });
  private _queryPersistence: MatTableQueryPersistence | null = null;
  /** Bumped whenever column order/definitions change so views re-bind row defs. */
  private readonly _columnLayoutEpoch = signal(0);

  // Query state
  private readonly _query = signal<MatTableQuery>({
    skip: 0,
    limit: 10,
    filters: {},
  });

  // Data fetching
  private _fetchFn?: MatTableFetchFn<T>;
  private _queryFn?: MatTableQueryFn;
  private _formatter?: (rows: T[]) => any[]; // Allow formatter to return different type
  private _formatterReplacesType = signal<boolean>(true); // Default: formatted data replaces original type
  /** Incremented on each fetch so stale debounced responses cannot overwrite newer data. */
  private _fetchGeneration = 0;
  private _originalData = signal<T[]>([]); // Store original data when formatter doesn't replace type

  // Computed values
  readonly columns = computed(() => {
    const prefix = this._columnIdPrefix();
    return this._columns().map(
      col =>
        ({
          ...col,
          id: `${prefix}-${col.id}`,
          originalId: col.id, // Store original ID for reference
        }) as MatTableColumn<T> & { originalId: string }
    );
  });
  readonly columnIdPrefix = computed(() => this._columnIdPrefix());

  /**
   * Get original column ID from prefixed ID
   */
  getOriginalColumnId(prefixedId: string): string {
    const prefix = this._columnIdPrefix();
    if (prefixedId.startsWith(`${prefix}-`)) {
      return prefixedId.substring(prefix.length + 1);
    }
    return prefixedId; // Return as-is if not prefixed
  }

  /**
   * Get prefixed column ID from original ID
   */
  getPrefixedColumnId(originalId: string): string {
    const prefix = this._columnIdPrefix();
    return `${prefix}-${originalId}`;
  }
  readonly pagination = computed(() => this._pagination());
  readonly rowExpansion = computed(() => this._rowExpansion());
  readonly childRows = computed(() => this._childRows());
  readonly selection = computed(() => this._selection());
  readonly actions = computed(() => {
    this._actionsVisibilityRevision();
    return this._actions();
  });
  readonly appearance = computed(() => this._appearance());
  readonly dragDrop = computed(() => this._dragDrop());
  readonly columnReorder = computed(() => this._columnReorder());
  readonly columnResizePersist = computed(() => this._columnResizePersist());
  readonly columnLayoutEpoch = computed(() => this._columnLayoutEpoch());
  readonly query = computed(() => this._query());

  // Check if actions should be shown (enabled and has visible actions)
  readonly hasVisibleActions = computed(() => {
    this._actionsVisibilityRevision();
    const actionsConfig = this._actions();
    if (!actionsConfig.enabled) {
      return false;
    }

    // If using a template, actions are visible
    if (actionsConfig.template) {
      return true;
    }

    // If using actions array, check if at least one action is visible
    if (!actionsConfig.actions) {
      return false;
    }

    // Check if at least one action is visible
    // Note: We need to use untracked() here since visible() functions might access signals
    return actionsConfig.actions.some(action => {
      if (typeof action.visible === 'function') {
        return action.visible(undefined as T);
      }
      return action.visible !== false;
    });
  });

  // Display columns (includes expansion, child toggle, and actions columns)
  readonly displayedColumns = computed(() => {
    const cols: string[] = [];
    const prefix = this._columnIdPrefix();

    // Selection column comes first (if enabled)
    if (this._selection().enabled) {
      cols.push('select');
    }

    // Row number comes next
    if (this._appearance().showRowNumbers) {
      cols.push('rowNumber');
    }

    // Expand column for row details (comes before child toggle)
    if (
      this._rowExpansion().enabled &&
      !this._rowExpansion().hideExpandColumn
    ) {
      cols.push('expand');
    }

    // Child toggle column for child rows feature - only show if not embedding in first column
    if (
      this._childRows().enabled &&
      !this._childRows().embedToggleInFirstColumn &&
      !this._childRows().hideBuiltInChildToggle
    ) {
      cols.push('childToggle');
    }

    // Use prefixed column IDs
    cols.push(
      ...this._columns()
        .filter(col => !col.hidden)
        .map(col => `${prefix}-${col.id}`)
    );

    // Only add actions column if enabled and has visible actions
    if (this.hasVisibleActions()) {
      cols.push('actions');
    }

    return cols;
  });

  // Sticky columns
  readonly stickyColumns = computed(() =>
    this._columns()
      .filter(col => col.sticky)
      .map(col => col.id)
  );

  readonly stickyEndColumns = computed(() => {
    const cols = this._columns()
      .filter(col => col.stickyEnd)
      .map(col => col.id);
    if (this._actions().enabled && this._actions().sticky) {
      cols.push('actions');
    }
    return cols;
  });

  /**
   * Set column ID prefix (useful for nested tables that should share the same prefix)
   */
  setColumnIdPrefix(prefix: string): this {
    this._columnIdPrefix.set(prefix);
    return this;
  }

  /**
   * Get the current column ID prefix
   */
  getColumnIdPrefix(): string {
    return this._columnIdPrefix();
  }

  /**
   * Generate a UUID fallback for environments without crypto.randomUUID
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Set table columns
   */
  setColumns(columns: MatTableColumn<T>[]): this {
    this._columns.set(uniqueColumnsById(columns));
    this._columnLayoutEpoch.update(epoch => epoch + 1);
    return this;
  }

  /** Column definitions as configured (without runtime id prefix). */
  getColumnDefinitions(): MatTableColumn<T>[] {
    return this._columns();
  }

  /** Visible data column ids in current render order (unprefixed). */
  getVisibleDataColumnIds(): string[] {
    return this._columns()
      .filter(col => !col.hidden)
      .map(col => col.id);
  }

  /**
   * Reorders visible data columns and returns their ids in the new order.
   * Hidden columns are kept after visible ones.
   */
  reorderDataColumns(
    previousIndex: number,
    currentIndex: number
  ): string[] | null {
    if (previousIndex === currentIndex) return null;

    const visible = this._columns().filter(col => !col.hidden);
    if (
      previousIndex < 0 ||
      currentIndex < 0 ||
      previousIndex >= visible.length ||
      currentIndex >= visible.length
    ) {
      return null;
    }

    const next = [...visible];
    const [moved] = next.splice(previousIndex, 1);
    next.splice(currentIndex, 0, moved);

    const hidden = this._columns().filter(col => col.hidden);
    this._columns.set([...next, ...hidden]);
    this._columnLayoutEpoch.update(epoch => epoch + 1);
    return next.map(col => col.id);
  }

  /**
   * Add a column to the table
   */
  addColumn(column: MatTableColumn<T>): this {
    this._columns.update(cols => [...cols, column]);
    return this;
  }

  /**
   * Set the data source with fetch and query transform functions
   */
  setDataSource(fetchFn: MatTableFetchFn<T>, queryFn?: MatTableQueryFn): this {
    this._fetchFn = fetchFn;
    this._queryFn = queryFn;
    return this;
  }

  /**
   * Set a formatter function to transform data before display
   * @param formatter - Function to transform rows
   * @param replaceType - If true, formatted data replaces the original type. If false, formatted data is stored in a separate field while keeping original type.
   */
  setFormatter(formatter: (rows: T[]) => any[], replaceType = true): this {
    this._formatter = formatter;
    this._formatterReplacesType.set(replaceType);
    return this;
  }

  /**
   * Enable pagination with configuration.
   * Omit arguments to use {@link DEFAULT_TABLE_PAGE_SIZE} and {@link TABLE_PAGE_SIZE_OPTIONS}.
   */
  withPagination(pageSize?: number, pageSizeOptions?: number[]): this {
    const resolvedPageSize = pageSize ?? DEFAULT_TABLE_PAGE_SIZE;
    const resolvedPageSizeOptions = pageSizeOptions ?? [
      ...TABLE_PAGE_SIZE_OPTIONS,
    ];

    this._pagination.set({
      enabled: true,
      pageSize: resolvedPageSize,
      pageSizeOptions: resolvedPageSizeOptions,
      showFirstLastButtons: true,
    });
    this._query.update(q => ({ ...q, limit: resolvedPageSize }));
    return this;
  }

  /**
   * Enable row expansion with optional detail component
   */
  withRowExpansion(
    detailComponent?: Type<MatRowDetailComponent<T>>,
    options?: Partial<MatTableRowExpansionConfig<T>>
  ): this {
    this._rowExpansion.set({
      enabled: true,
      expandOnRowClick: options?.expandOnRowClick ?? true,
      multipleExpanded: options?.multipleExpanded ?? false,
      hideExpandColumn: options?.hideExpandColumn ?? false,
      detailComponent,
      detailHeight: options?.detailHeight ?? 'auto',
      expansionHeaderTemplate: options?.expansionHeaderTemplate,
      canExpand: options?.canExpand,
    });
    return this;
  }

  /**
   * Returns true when the given row is allowed to open the row-detail panel.
   * Falls back to true when no `canExpand` predicate is configured.
   */
  canExpandRow(row: T): boolean {
    const predicate = this._rowExpansion().canExpand;
    return predicate ? predicate(row) : true;
  }

  /**
   * Enable child rows feature
   */
  withChildRows(config: Partial<MatTableChildRowsConfig<T>>): this {
    this._childRows.set({
      enabled: true,
      getChildren: config.getChildren || (() => undefined),
      isParent: config.isParent || (() => false),
      isChild: config.isChild || (() => false),
      // If caller doesn't provide getParent, fall back to internal parent map
      getParent:
        config.getParent ||
        ((row: T) => {
          return this.childParentMap.get(row);
        }),
      toggleOnRowClick: config.toggleOnRowClick ?? true,
      multipleExpanded: config.multipleExpanded ?? true,
      parentRowClass: config.parentRowClass,
      childRowClass: config.childRowClass,
      indentChildren: config.indentChildren ?? true,
      indentAmount: config.indentAmount ?? 40,
      embedToggleInFirstColumn: config.embedToggleInFirstColumn ?? false,
      hideBuiltInChildToggle: config.hideBuiltInChildToggle ?? false,
      startCollapsed: config.startCollapsed ?? false,
      showTreeToggleIconWhen: config.showTreeToggleIconWhen,
    });
    return this;
  }

  /**
   * Enable actions column
   */
  withActions(config: Partial<MatTableActionsConfig<T>>): this {
    this._actions.set({
      enabled: true,
      ...config,
    });
    return this;
  }

  /**
   * Set table appearance
   */
  setAppearance(appearance: Partial<MatTableAppearance>): this {
    this._appearance.update(current => ({ ...current, ...appearance }));
    return this;
  }

  /**
   * Enable drag-drop functionality for row reordering
   */
  withDragDrop(
    enabled: boolean,
    onDragDrop?: (
      event: import('@angular/cdk/drag-drop').CdkDragDrop<T[]>
    ) => void
  ): this {
    this._dragDrop.set({
      enabled,
      onDragDrop,
    });
    return this;
  }

  /** Enable drag-and-drop reordering of data columns via the header row. */
  withColumnReorder(
    enabled: boolean,
    onReorder?: (columnIds: string[]) => void
  ): this {
    this._columnReorder.set({
      enabled,
      onReorder,
    });
    return this;
  }

  /** Notify when a data column resize completes (unprefixed column id, px width). */
  withColumnResizePersist(
    enabled: boolean,
    onColumnResize?: (columnId: string, widthPx: number) => void
  ): this {
    this._columnResizePersist.set({
      enabled,
      onColumnResize,
    });
    return this;
  }

  withQueryPersistence(handler: MatTableQueryPersistence | null): this {
    this._queryPersistence = handler;
    return this;
  }

  private persistQueryState(): void {
    if (!this._queryPersistence) return;
    const query = this._query();
    this._queryPersistence.persist({
      filters: { ...(query.filters ?? {}) },
      sortField: query.sort_field,
      sortOrder: query.sort_order,
      pageSize: query.limit > 0 ? query.limit : DEFAULT_TABLE_PAGE_SIZE,
    });
  }

  /**
   * Update query parameters
   */
  setQuery(query: Partial<MatTableQuery>): this {
    this._query.update(current => ({ ...current, ...query }));
    if (typeof query.limit === 'number' && query.limit > 0) {
      const limit = query.limit;
      this._pagination.update(config =>
        config.pageSize === limit ? config : { ...config, pageSize: limit }
      );
    }
    return this;
  }

  /**
   * Set page index (for server-side pagination)
   */
  setPage(pageIndex: number): void {
    const pageSize = this._query().limit;
    this.setQuery({ skip: pageIndex * pageSize });
    this.fetch();
  }

  /**
   * Set page size (for server-side pagination)
   */
  setPageSize(pageSize: number): void {
    this.setQuery({ skip: 0, limit: pageSize });
    this.persistQueryState();
    this.fetch();
  }

  /**
   * Set sorting
   * Accepts both prefixed and original column IDs
   */
  setSort(field: string | null, direction: SortOrderEnum): void {
    if (!field || !direction) {
      this.setQuery({ sort_field: undefined, sort_order: undefined });
    } else {
      // Get original ID in case field is prefixed
      const originalId = this.getOriginalColumnId(field);
      // Find the column by original ID (since _columns stores original IDs)
      const column = this._columns().find(col => col.id === originalId);
      const sortField = column?.sortField || originalId;
      this.setQuery({ sort_field: sortField, sort_order: direction });
    }
    this.persistQueryState();
    this.fetch();
  }

  /**
   * Set filter for a column (server-side filtering)
   * Accepts both prefixed and original column IDs
   */
  setFilter(columnId: string, filter: MatTableFilter | null): void {
    // Strip prefix if present to use original ID for filtering
    const originalId = this.getOriginalColumnId(columnId);
    this._query.update(query => {
      const filters = { ...query.filters };
      if (filter) {
        filters[originalId] = filter;
      } else {
        delete filters[originalId];
      }
      return { ...query, filters, skip: 0 }; // Reset to first page
    });
    this.persistQueryState();
    this.fetch();
  }

  /**
   * Clear all filters (server-side filtering)
   */
  clearFilters(): void {
    this._query.update(query => ({ ...query, filters: {}, skip: 0 }));
    this.persistQueryState();
    this.fetch();
  }

  /**
   * Toggle row expansion
   */
  toggleRow(row: T): void {
    const config = this._rowExpansion();
    if (!config.enabled) return;

    this.expandedRows.update(expanded => {
      const newExpanded = new Set(expanded);

      if (newExpanded.has(row)) {
        newExpanded.delete(row);
      } else {
        if (!config.multipleExpanded) {
          newExpanded.clear();
        }
        newExpanded.add(row);
      }

      return newExpanded;
    });
  }

  /**
   * Check if row is expanded
   */
  isRowExpanded(row: T): boolean {
    return this.expandedRows().has(row);
  }

  /**
   * Collapse all rows
   */
  collapseAll(): void {
    this.expandedRows.set(new Set());
  }

  /**
   * Toggle child rows visibility for a parent row
   */
  toggleChildRows(parentRow: T): void {
    const config = this._childRows();
    if (!config.enabled || !config.isParent(parentRow)) return;

    this.collapsedParents.update(collapsed => {
      const newCollapsed = new Set(collapsed);

      if (newCollapsed.has(parentRow)) {
        // Expand: remove from collapsed set
        newCollapsed.delete(parentRow);
      } else {
        // Collapse: add to collapsed set
        if (!config.multipleExpanded) {
          // If only one can be expanded, collapse all others
          newCollapsed.clear();
        }
        newCollapsed.add(parentRow);
      }

      return newCollapsed;
    });
  }

  /**
   * Check if child rows are visible for a parent row
   */
  areChildRowsVisible(parentRow: T): boolean {
    return !this.collapsedParents().has(parentRow);
  }

  /**
   * Nesting depth for tree rows (0 = root). Used for mat-tree-style indentation.
   */
  getRowTreeDepth(row: T): number {
    const config = this._childRows();
    if (!config.enabled || !config.getParent) {
      return 0;
    }
    const getP = config.getParent;
    let depth = 0;
    let current: T | undefined = row;
    const seen = new Set<T>();
    while (current != null) {
      const parent = getP(current);
      if (parent == null) {
        break;
      }
      if (seen.has(parent)) {
        break;
      }
      seen.add(parent);
      depth += 1;
      current = parent;
    }
    return depth;
  }

  /** True when this row has a parent in the flattened child-rows tree (not a root row). */
  rowHasTreeParent(row: T): boolean {
    return this.childParentMap.has(row);
  }

  /**
   * Check if a row should be visible based on child rows configuration
   */
  isRowVisible(row: T): boolean {
    const config = this._childRows();
    if (!config.enabled) return true;

    // Root rows are always visible
    if (!this.rowHasTreeParent(row)) return true;

    // Nested rows: every ancestor must be expanded
    if (config.getParent) {
      let current: T | undefined = row;
      const seen = new Set<T>();
      while (current != null) {
        const parent = config.getParent(current);
        if (!parent) break;
        if (seen.has(parent)) break;
        seen.add(parent);
        if (!this.areChildRowsVisible(parent)) return false;
        current = parent;
      }
    }

    return true;
  }

  /**
   * Get visible data (filtered by child rows visibility)
   */
  readonly visibleData = computed(() => {
    const config = this._childRows();
    const allData = this.data();

    if (!config.enabled) return allData;

    return allData.filter(row => this.isRowVisible(row));
  });

  /**
   * Flatten root rows (when child-rows tree is enabled), update table data and count.
   * Used by {@link fetch} and by subclasses for silent refresh on hierarchical tables.
   */
  protected applyRootFetchResult(
    rootRows: T[],
    count: number,
    options?: { applyStartCollapsed?: boolean }
  ): void {
    let rows = rootRows;
    const originalRows = [...rows];

    if (this._formatter) {
      const formattedRows = this._formatter(rows);

      if (this._formatterReplacesType()) {
        rows = formattedRows;
      } else {
        rows = rows.map((originalRow, index) => {
          const formattedRow = formattedRows[index];
          return {
            ...originalRow,
            $formatted: formattedRow,
          } as T & { $formatted: T };
        }) as T[];
      }
    }

    let flatRows = rows;
    const childRowsConfig = this._childRows();
    this.childParentMap.clear();

    if (childRowsConfig.enabled && childRowsConfig.getChildren) {
      const result: T[] = [];

      const visit = (row: T, parent: T | null) => {
        if (parent) {
          this.childParentMap.set(row, parent);
        }
        result.push(row);

        const children = childRowsConfig.getChildren!(row);
        if (Array.isArray(children) && children.length > 0) {
          for (const child of children) {
            visit(child, row);
          }
        }
      };

      for (const root of rows) {
        visit(root, null);
      }

      flatRows = result;
    }

    const applyStartCollapsed = options?.applyStartCollapsed ?? true;
    if (
      applyStartCollapsed &&
      childRowsConfig.enabled &&
      childRowsConfig.startCollapsed
    ) {
      const parents = flatRows.filter(row => childRowsConfig.isParent(row));
      if (parents.length > 0) {
        this.collapsedParents.set(new Set(parents));
      }
    }

    untracked(() => {
      this.data.set(flatRows);
      this._originalData.set(originalRows);
      this.totalCount.set(count || flatRows.length);
    });
  }

  /**
   * Fetch data from server
   * Preserves existing data during fetch to prevent table collapse
   */
  async fetch(force = false): Promise<void> {
    if (!force && this.isLoading()) {
      return;
    }

    if (!this._fetchFn) {
      return;
    }

    // CRITICAL: Preserve current data - NEVER clear it before fetching
    // Keep the reference to prevent Material Table from removing rows from DOM
    const previousData = this.data();

    // Ensure data signal always has data (never empty array during fetch)
    // This prevents Material Table from removing the table from DOM
    if (previousData.length === 0) {
      // If no data exists, set a placeholder to keep table structure
      // This will be replaced when real data arrives
      this.data.set([]);
    }

    const fetchGeneration = ++this._fetchGeneration;
    this.isLoading.set(true);

    try {
      const query = this._queryFn
        ? this._queryFn(this._query())
        : this._query();

      const response = await new Promise<{ rows: T[]; count: number }>(
        (resolve, reject) => {
          this._fetchFn!(query as any)
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(300))
            .subscribe({
              next: resolve,
              error: reject,
            });
        }
      );

      if (fetchGeneration !== this._fetchGeneration) {
        return;
      }

      this.applyRootFetchResult(response.rows || [], response.count ?? 0);
    } catch (error) {
      if (fetchGeneration !== this._fetchGeneration) {
        return;
      }
      console.error('MatTableService: fetch error', error);
      // On error, preserve previous data instead of clearing
      // Only clear if there was no previous data
      if (previousData.length === 0) {
        this.data.update(() => []);
        this.totalCount.set(0);
      }
      // Otherwise keep the previous data visible - don't update the signal
      throw error;
    } finally {
      if (fetchGeneration === this._fetchGeneration) {
        this.isLoading.set(false);
      }
    }
  }

  /**
   * Refresh the table data
   */
  refresh(): void {
    this.fetch(true);
  }

  /**
   * Same fetch as refresh(), without the loading overlay.
   * Keeps the current rows when the request fails.
   */
  async silentRefresh(): Promise<void> {
    if (!this._fetchFn) {
      return;
    }

    const previousData = this.data();
    const previousCount = this.totalCount();
    try {
      const query = this._queryFn
        ? this._queryFn(this._query())
        : this._query();
      const response = await firstValueFrom(this._fetchFn(query as never));
      this.applyRootFetchResult(response.rows || [], response.count ?? 0);
    } catch {
      this.data.set(previousData);
      this.totalCount.set(previousCount);
    }
  }

  /**
   * Re-evaluate `hasVisibleActions` / actions column when `visible` depends on signals that live outside this service (e.g. permissions).
   */
  refreshActionVisibility(): void {
    this._actionsVisibilityRevision.update(n => n + 1);
  }

  /**
   * Reset table to initial state
   */
  reset(): void {
    this.setQuery({
      skip: 0,
      limit: this._pagination().pageSize,
      sort_field: undefined,
      sort_order: undefined,
      filters: {},
    });
    this.collapseAll();
    this.selectedRows.set(new Set());
    this.currentSelectionRow.set(null);
    this.fetch();
  }

  /**
   * Configure row selection
   */
  withSelection(config: Partial<MatTableSelectionConfig<T>>): this {
    this._selection.update(current => ({
      ...current,
      ...config,
      enabled: config.enabled !== undefined ? config.enabled : current.enabled,
    }));
    return this;
  }

  /**
   * Toggle row selection
   */
  toggleRowSelection(row: T): void {
    if (!this._selection().enabled) {
      return;
    }

    const currentSelection = new Set(this.selectedRows());
    const isSelected = currentSelection.has(row);

    if (isSelected) {
      currentSelection.delete(row);
      if (this.currentSelectionRow() === row) {
        this.currentSelectionRow.set(null);
      }
    } else {
      // If single selection mode, clear previous selection
      if (this._selection().mode === 'single') {
        currentSelection.clear();
      }
      currentSelection.add(row);
      this.currentSelectionRow.set(row);
    }

    this.selectedRows.set(currentSelection);
    this.notifySelectionChange();
  }

  /**
   * Check if a row is selected
   */
  isRowSelected(row: T): boolean {
    return this.selectedRows().has(row);
  }

  /**
   * Get selected rows as an array
   */
  getSelectedRows(): T[] {
    return Array.from(this.selectedRows());
  }

  /**
   * Clear all selections
   */
  clearSelection(): void {
    this.selectedRows.set(new Set());
    this.currentSelectionRow.set(null);
    this.notifySelectionChange();
  }

  /**
   * Select all rows on the current page (for multiple selection header checkbox)
   */
  selectAllOnPage(rows: T[]): void {
    if (!this._selection().enabled || this._selection().mode !== 'multiple') {
      return;
    }
    const next = new Set(this.selectedRows());
    rows.forEach(row => next.add(row));
    this.selectedRows.set(next);
    this.currentSelectionRow.set(null); // No single "current" when selecting all
    this.notifySelectionChange();
  }

  /**
   * Deselect all rows on the current page
   */
  deselectAllOnPage(rows: T[]): void {
    if (!this._selection().enabled) {
      return;
    }
    const next = new Set(this.selectedRows());
    rows.forEach(row => next.delete(row));
    this.selectedRows.set(next);
    const current = this.currentSelectionRow();
    if (current && !next.has(current)) {
      this.currentSelectionRow.set(null);
    }
    this.notifySelectionChange();
  }

  /**
   * Notify selection change callback
   */
  private notifySelectionChange(): void {
    const config = this._selection();
    if (config.onSelectionChange) {
      config.onSelectionChange(this.getSelectedRows());
    }
  }

  /**
   * Get cell value from row using dataPath
   * When formatter doesn't replace type, checks $formatted field first.
   * Applies `column.valueFormatter` if defined before returning.
   */
  getCellValue(row: T, column: MatTableColumn<T>): unknown {
    // Get original column ID (strip prefix if present)
    const originalId =
      (column as MatTableColumn<T> & { originalId?: string }).originalId ||
      this.getOriginalColumnId(column.id);

    const formatted = (row as { $formatted?: unknown }).$formatted;
    const source =
      !this._formatterReplacesType() && formatted != null ? formatted : row;

    if (column.dataPath) {
      const resolved = readColumnPath(source, column.dataPath);
      return formatResolvedColumnValue(resolved, column.valueFormatter);
    }

    const value = (source as Record<string, unknown> | null | undefined)?.[
      originalId
    ];
    return column.valueFormatter ? column.valueFormatter(value) : value;
  }

  /**
   * Get current page index
   */
  getCurrentPageIndex(): number {
    return Math.floor(this._query().skip / this._query().limit);
  }

  computeHeightWithLimit(length: number, maxLength = 5): string {
    const headerHeight = 48;
    const rowHeight = 40;
    const horizontalScrollbarGutter = 16;

    if (length < 1) {
      return `${90 + horizontalScrollbarGutter}px`;
    }

    const visibleRows = Math.min(length, maxLength);
    return `${headerHeight + visibleRows * rowHeight + horizontalScrollbarGutter}px`;
  }

  /**
   * Cleanup on destroy
   */
  onDestroy(): void {
    this.data.set([]);
    this.expandedRows.set(new Set());
    this.selectedRows.set(new Set());
    this.currentSelectionRow.set(null);
    this.collapsedParents.set(new Set());
  }
}
